// ============================================
// FILE: /frontend/src/components/facebook/BulkCollageGenerator.jsx
// PURPOSE: Generate PROFESSIONAL card-style collage for Facebook
// MODIFIED: Grid layout - same as WhatsApp version
// ============================================

import { useState, useEffect, useRef } from 'react';
import { 
  CheckSquare, 
  Square, 
  Download,
  Share2,
  Loader2,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Eye,
  Check
} from 'lucide-react';
import { useToast } from '../../hooks/useToast';
import { useAuth } from '../../hooks/useAuth';
import { facebookAPI } from '../../api/facebook.api';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Alert, AlertDescription } from '../ui/alert';
import { Badge } from '../ui/badge';

export default function BulkCollageGenerator({ 
  listings = [],
  onMessageGenerated 
}) {
  const { toast } = useToast();
  const { user } = useAuth();
  const canvasRef = useRef(null);
  
  const [selectedIds, setSelectedIds] = useState([]);
  const [isListingsExpanded, setIsListingsExpanded] = useState(true);
  const [loadedImages, setLoadedImages] = useState({});
  const [generating, setGenerating] = useState(false);
  const [generatedData, setGeneratedData] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [imageDownloaded, setImageDownloaded] = useState(false);
  const [messageCopied, setMessageCopied] = useState(false);

    const getFullImageUrl = (imageUrl) => {
    if (!imageUrl || typeof imageUrl !== 'string') return null;
    if (imageUrl.startsWith('http')) return imageUrl;
    const backendUrl = (import.meta.env.VITE_BASE_URL || 'http://localhost:5000');
    return `${backendUrl}${imageUrl}`;
  };

  // Helper to get first image from listing
  const getListingImageUrl = (listing) => {
    if (!listing) return null;
    
    // Handle imageUrl as array format (NEW SCHEMA)
    if (Array.isArray(listing.imageUrl) && listing.imageUrl.length > 0) {
      return listing.imageUrl[0];
    }
    
    // Fallback to imageUrl as string (OLD SCHEMA - backwards compatibility)
    if (typeof listing.imageUrl === 'string') {
      return listing.imageUrl;
    }
    
    return null;
  };

  useEffect(() => {
    if (selectedIds.length === 0) {
      setLoadedImages({});
      return;
    }

    const selectedListings = listings.filter(l => selectedIds.includes(l.id));
    
      selectedListings.forEach(listing => {
      if (loadedImages[listing.id]) return;
      
      const imageUrl = getListingImageUrl(listing);
      const fullUrl = getFullImageUrl(imageUrl);
      if (!fullUrl) return;

      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        setLoadedImages(prev => ({
          ...prev,
          [listing.id]: img
        }));
      };
      img.onerror = () => {
        console.error('[BulkCollageGenerator] ❌ Failed to load:', fullUrl);
      };
      img.src = fullUrl;
    });
  }, [selectedIds, listings]);

  useEffect(() => {
    if (!showPreview || !canvasRef.current || selectedIds.length === 0) return;
    
    const selectedListings = listings.filter(l => selectedIds.includes(l.id));
    const allImagesLoaded = selectedListings.every(l => loadedImages[l.id]);
    
    if (allImagesLoaded) {
      drawCollage();
    }
  }, [showPreview, selectedIds, loadedImages]);

  // Draw GRID-STYLE collage
  const drawCollage = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const selectedListings = listings.filter(l => selectedIds.includes(l.id));
    const dealerName = user?.fullName || 'MotoHome Dealer';

    // Grid dimensions
    const cardWidth = 580;
    const cardHeight = 400;
    const gap = 20;
    const padding = 30;
    const borderRadius = 16;
    const headerHeight = 100;
    const footerHeight = 100;

   // Calculate number of rows needed based on selected listings
    const numListings = selectedListings.length;
    let numRows;
    if (numListings === 1) {
      numRows = 1; // Single card centered
    } else if (numListings === 2) {
      numRows = 1; // Two cards side by side
    } else if (numListings <= 4) {
      numRows = 2; // Large card + middle row
    } else {
      numRows = 3; // All three rows
    }

    // Canvas size: 2 columns x dynamic rows + header + footer
    const canvasWidth = (cardWidth * 2) + (gap * 1) + (padding * 2);
    const canvasHeight = headerHeight + gap + (cardHeight * numRows) + (gap * (numRows - 1)) + gap + footerHeight + (padding * 2);

    canvas.width = canvasWidth;
    canvas.height = canvasHeight;

    const ctx = canvas.getContext('2d');

    // Background - clean white
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvasWidth, canvasHeight);

    // Helper function to draw rounded rectangle
    const drawRoundedRect = (x, y, w, h, r) => {
      ctx.beginPath();
      ctx.moveTo(x + r, y);
      ctx.lineTo(x + w - r, y);
      ctx.quadraticCurveTo(x + w, y, x + w, y + r);
      ctx.lineTo(x + w, y + h - r);
      ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
      ctx.lineTo(x + r, y + h);
      ctx.quadraticCurveTo(x, y + h, x, y + h - r);
      ctx.lineTo(x, y + r);
      ctx.quadraticCurveTo(x, y, x + r, y);
      ctx.closePath();
    };

    // HEADER - white background, black text, rounded corners with border
    const headerRadius = 12;
    ctx.fillStyle = '#ffffff';
    
    drawRoundedRect(padding, padding, canvasWidth - (padding * 2), headerHeight, headerRadius);
    ctx.fill();
    
    // Border for header
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 4;
    ctx.stroke();
    
    // Dealer name - LEFT aligned - BLACK TEXT
    ctx.fillStyle = '#000000';
    ctx.font = 'bold 48px Arial, sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText(dealerName, padding + 25, padding + 45);
    
    // Subtitle - LEFT aligned - BLACK TEXT
    ctx.font = '28px Arial, sans-serif';
    ctx.fillStyle = '#000000';
    ctx.fillText('Latest Deals - Premium Selection', padding + 25, padding + 80);

    // Helper function to draw single listing card
    const drawListingCard = (listing, x, y, w, h) => {
      // NO border/background here - drawn by parent
      
      // Image (top 60% of card)
      const img = loadedImages[listing.id];
      const imageHeight = h * 0.6;
      const imageRadius = 12;
      
      if (img) {
        const scale = Math.max(w / img.width, imageHeight / img.height);
        const scaledW = img.width * scale;
        const scaledH = img.height * scale;
        const imgX = x + (w - scaledW) / 2;
        const imgY = y + (imageHeight - scaledH) / 2;
        
        ctx.save();
        // Clip to rounded rectangle
        drawRoundedRect(x + 5, y + 5, w - 10, imageHeight - 10, imageRadius);
        ctx.clip();
        ctx.drawImage(img, imgX, imgY, scaledW, scaledH);
        ctx.restore();
        
        // Image border
        ctx.strokeStyle = '#e0e0e0';
        ctx.lineWidth = 2;
        drawRoundedRect(x + 5, y + 5, w - 10, imageHeight - 10, imageRadius);
        ctx.stroke();
      } else {
        // Placeholder
        ctx.fillStyle = '#f5f5f5';
        drawRoundedRect(x + 5, y + 5, w - 10, imageHeight - 10, imageRadius);
        ctx.fill();
      }

      // Details (bottom 40% of card)
      const detailsY = y + imageHeight + 25; // ADDED GAP
      const detailsX = x + 15;
      const detailsWidth = w - 30;

      // Title
      ctx.fillStyle = '#1a1a1a';
      ctx.font = 'bold 24px Arial, sans-serif';
      ctx.textAlign = 'left';
      
      const titleWords = listing.title.split(' ');
      let titleLine = '';
      let titleY = detailsY;
      
      titleWords.forEach((word, idx) => {
        const testLine = titleLine + word + ' ';
        const metrics = ctx.measureText(testLine);
        if (metrics.width > detailsWidth && titleLine !== '') {
          ctx.fillText(titleLine.trim(), detailsX, titleY);
          titleLine = word + ' ';
          titleY += 28;
        } else {
          titleLine = testLine;
        }
        if (idx === titleWords.length - 1) {
          ctx.fillText(titleLine.trim(), detailsX, titleY);
        }
      });

      // Price
      ctx.fillStyle = '#2e7d32';
      ctx.font = 'bold 28px Arial, sans-serif';
      ctx.fillText(`KES ${parseFloat(listing.price).toLocaleString()}`, detailsX, detailsY + 65);

      // Location
      ctx.fillStyle = '#666';
      ctx.font = '18px Arial, sans-serif';
      ctx.fillText(`${listing.subCounty}, ${listing.county}`, detailsX, detailsY + 95);
    };

    // GRID LAYOUT
    const gridStartY = padding + headerHeight + gap;
    
    // Top-left: LARGE CARD with 2 sub-cards - NO BORDER
    const largeCardX = padding;

    // SPECIAL LAYOUTS FOR 1 AND 2 IMAGES
    if (numListings === 1) {
      // Single large centered card
      const singleCardW = cardWidth * 1.5;
      const singleCardH = cardHeight * 1.5;
      const singleCardX = padding + (canvasWidth - padding * 2 - singleCardW) / 2;
      const singleCardY = gridStartY;
      
      ctx.fillStyle = '#ffffff';
      drawRoundedRect(singleCardX, singleCardY, singleCardW, singleCardH, borderRadius);
      ctx.fill();
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 4;
      ctx.stroke();
      drawListingCard(selectedListings[0], singleCardX, singleCardY, singleCardW, singleCardH);
    } else if (numListings === 2) {
      // Two cards side by side
      const card1X = padding;
      const card2X = padding + cardWidth + gap;
      
      // Card 1
      ctx.fillStyle = '#ffffff';
      drawRoundedRect(card1X, gridStartY, cardWidth, cardHeight, borderRadius);
      ctx.fill();
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 4;
      ctx.stroke();
      drawListingCard(selectedListings[0], card1X, gridStartY, cardWidth, cardHeight);
      
      // Card 2
      ctx.fillStyle = '#ffffff';
      drawRoundedRect(card2X, gridStartY, cardWidth, cardHeight, borderRadius);
      ctx.fill();
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 4;
      ctx.stroke();
      drawListingCard(selectedListings[1], card2X, gridStartY, cardWidth, cardHeight);
    } else {
    
    // Top-left: LARGE CARD with 2 sub-cards
    const largeCardX = padding;
    const largeCardY = gridStartY;
    const largeCardW = cardWidth;
    const largeCardH = (cardHeight * 2) + gap;

    // Large card background - NO BORDER
    ctx.fillStyle = '#f9f9f9';
    drawRoundedRect(largeCardX, largeCardY, largeCardW, largeCardH, borderRadius);
    ctx.fill();
    // NO STROKE - no border on large container

    // Sub-card 1 (top half of large card)
    if (selectedListings[0]) {
      drawListingCard(
        selectedListings[0],
        largeCardX + 10,
        largeCardY + 10,
        largeCardW - 20,
        cardHeight - 10
      );
    }

    // Sub-card 2 (bottom half of large card)
    if (selectedListings[1]) {
      drawListingCard(
        selectedListings[1],
        largeCardX + 10,
        largeCardY + cardHeight + gap,
        largeCardW - 20,
        cardHeight - 10
      );
    }

    // Top-right: Standard card WITH BLACK BORDER
    if (selectedListings[2]) {
      const x = padding + cardWidth + gap;
      const y = gridStartY;
      
      // Card background
      ctx.fillStyle = '#ffffff';
      drawRoundedRect(x, y, cardWidth, cardHeight, borderRadius);
      ctx.fill();
      
      // BLACK BORDER
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 4;
      ctx.stroke();
      
      // Draw listing content
      drawListingCard(selectedListings[2], x, y, cardWidth, cardHeight);
    }

    // Middle-right: Standard card WITH BLACK BORDER
    if (selectedListings[3]) {
      const x = padding + cardWidth + gap;
      const y = gridStartY + cardHeight + gap;
      
      // Card background
      ctx.fillStyle = '#ffffff';
      drawRoundedRect(x, y, cardWidth, cardHeight, borderRadius);
      ctx.fill();
      
      // BLACK BORDER
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 4;
      ctx.stroke();
      
      // Draw listing content
      drawListingCard(selectedListings[3], x, y, cardWidth, cardHeight);
    }

    // Bottom-left: Standard card WITH BLACK BORDER
    if (selectedListings[4]) {
      const x = padding;
      const y = gridStartY + (cardHeight * 2) + (gap * 2);
      
      // Card background
      ctx.fillStyle = '#ffffff';
      drawRoundedRect(x, y, cardWidth, cardHeight, borderRadius);
      ctx.fill();
      
      // BLACK BORDER
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 4;
      ctx.stroke();
      
      // Draw listing content
      drawListingCard(selectedListings[4], x, y, cardWidth, cardHeight);
    }

    // Bottom-right: Standard card WITH BLACK BORDER
    if (selectedListings[5]) {
      const x = padding + cardWidth + gap;
      const y = gridStartY + (cardHeight * 2) + (gap * 2);
      
      // Card background
      ctx.fillStyle = '#ffffff';
      drawRoundedRect(x, y, cardWidth, cardHeight, borderRadius);
      ctx.fill();
      
      // BLACK BORDER
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 4;
      ctx.stroke();
      
      // Draw listing content
      drawListingCard(selectedListings[5], x, y, cardWidth, cardHeight);
    }
  }
    // FOOTER - white background, black text, rounded corners with border
    const footerY = canvasHeight - footerHeight - padding;
    const footerRadius = 12;
    
    ctx.fillStyle = '#ffffff';
    drawRoundedRect(padding, footerY, canvasWidth - (padding * 2), footerHeight, footerRadius);
    ctx.fill();
    
    // Border for footer
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 4;
    ctx.stroke();
    
    // Branding text - LEFT aligned - BLACK TEXT
    ctx.fillStyle = '#000000';
    ctx.font = 'bold 42px Arial, sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText('Posted via MotoHome Finder', padding + 30, footerY + 60);

    console.log('[BulkCollageGenerator] ✅ Facebook grid collage complete!');
  };

  const toggleSelection = (listingId) => {
    setSelectedIds(prev => {
      if (prev.includes(listingId)) {
        return prev.filter(id => id !== listingId);
      } else {
        if (prev.length >= 6) {
          toast({
            variant: 'destructive',
            title: 'Maximum reached',
            description: 'You can select up to 6 listings'
          });
          return prev;
        }
        return [...prev, listingId];
      }
    });
  };

  const selectAll = () => {
    const allIds = listings.slice(0, 6).map(l => l.id);
    setSelectedIds(allIds);
  };

  const deselectAll = () => {
    setSelectedIds([]);
    setShowPreview(false);
    setGeneratedData(null);
    setLoadedImages({});
    setImageDownloaded(false);
    setMessageCopied(false);
  };

  const handleGenerate = async () => {
    if (selectedIds.length === 0) {
      toast({
        variant: 'destructive',
        title: 'No listings selected',
        description: 'Please select at least one listing'
      });
      return;
    }

// Check if all images are loaded
  const selectedListings = listings.filter(l => selectedIds.includes(l.id));
  const allImagesLoaded = selectedListings.every(l => loadedImages[l.id]);
  
  if (!allImagesLoaded) {
    toast({
      variant: 'destructive',
      title: 'Images still loading',
      description: 'Please wait for all images to load before generating'
    });
    return;
  }

  setGenerating(true);

    try {
      const response = await facebookAPI.generateBulkMessage(selectedIds);
      
      setGeneratedData({
        message: response.message,
        trackingIds: response.trackingIds,
        listingsCount: response.listingsCount
      });

      setShowPreview(true);
      setIsListingsExpanded(false);
      setImageDownloaded(false);
      setMessageCopied(false);
      
      toast({
        title: 'Collage ready!',
        description: `${response.listingsCount} listings ready to share on Facebook`
      });

      if (onMessageGenerated) {
        onMessageGenerated(response);
      }

    } catch (error) {
      console.error('[BulkCollageGenerator] ❌ Generate error:', error);
      
      if (error.response?.data?.error === 'UPGRADE_REQUIRED') {
        toast({
          variant: 'destructive',
          title: 'Upgrade Required',
          description: 'Facebook Tools are only available for Gold and Platinum members'
        });
      } else {
        toast({
          variant: 'destructive',
          title: 'Generation failed',
          description: error.response?.data?.message || 'Could not generate message'
        });
      }
    } finally {
      setGenerating(false);
    }
  };

  const handleDownloadCollage = () => {
    const canvas = canvasRef.current;
    const fileName = `${user?.fullName || 'MotoHome'}-Facebook-Post-${Date.now()}.png`;
    
    canvas.toBlob((blob) => {
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.download = fileName;
      link.href = url;
      link.click();
      URL.revokeObjectURL(url);
      
      setImageDownloaded(true);
      
      toast({
        title: '✅ Image downloaded!',
        description: 'Now copy the message below'
      });
    }, 'image/png', 1.0);
  };

  const handleCopyMessage = async () => {
    if (!generatedData?.message) return;
    
    try {
      await navigator.clipboard.writeText(generatedData.message);
      setMessageCopied(true);
      
      toast({
        title: '✅ Message copied!',
        description: 'Now open Facebook and paste'
      });
      
      setTimeout(() => {
        setMessageCopied(false);
      }, 3000);
    } catch (error) {
      console.error('Failed to copy message:', error);
      toast({
        variant: 'destructive',
        title: 'Copy failed',
        description: 'Please try again'
      });
    }
  };

  if (listings.length === 0) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription className="text-xs sm:text-sm">
          No approved listings available for Facebook sharing.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <Card className="shadow-sm">
        <CardHeader 
          className="p-3 sm:p-4 md:p-6 cursor-pointer hover:bg-accent/50 transition-colors"
          onClick={() => setIsListingsExpanded(!isListingsExpanded)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-3">
              <CardTitle className="text-sm sm:text-base md:text-lg">
                Select Listings ({selectedIds.length}/6)
              </CardTitle>
              {selectedIds.length > 0 && (
                <Badge variant="secondary" className="text-[10px] sm:text-xs">
                  {selectedIds.length} selected
                </Badge>
              )}
            </div>
            {isListingsExpanded ? (
              <ChevronUp className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
            ) : (
              <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
            )}
          </div>
        </CardHeader>

        {isListingsExpanded && (
          <CardContent className="p-3 sm:p-4 md:p-6 pt-0 space-y-3 sm:space-y-4">
            <div className="flex gap-2 sm:gap-3">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={selectAll}
                className="text-xs sm:text-sm h-9 sm:h-10"
              >
                <CheckSquare className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                Select First 6
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={deselectAll}
                className="text-xs sm:text-sm h-9 sm:h-10"
              >
                <Square className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                Clear All
              </Button>
            </div>

            <div className="space-y-2 sm:space-y-3">
              {listings.map((listing) => {
                const isSelected = selectedIds.includes(listing.id);
                const imageLoaded = loadedImages[listing.id];
                
                return (
                  <div
                    key={listing.id}
                    onClick={() => toggleSelection(listing.id)}
                    className={`flex items-start gap-2 sm:gap-3 p-2 sm:p-3 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                      isSelected 
                        ? 'border-primary bg-primary/5 shadow-sm' 
                        : 'border-border hover:border-primary/50 hover:shadow-sm'
                    }`}
                  >
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                      isSelected ? 'bg-primary border-primary' : 'border-muted-foreground'
                    }`}>
                      {isSelected && <CheckSquare className="w-4 h-4 text-white" />}
                    </div>
                    
                    <div className="flex gap-2 sm:gap-3 flex-grow min-w-0">
                    
                    {getListingImageUrl(listing) && (
                        <div className="relative">
                          <img
                            src={getFullImageUrl(getListingImageUrl(listing))}
                            alt={listing.title}
                            className="w-14 h-14 sm:w-16 sm:h-16 object-cover rounded"
                          />
                          {isSelected && !imageLoaded && (
                            <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded">
                              <Loader2 className="w-4 h-4 text-white animate-spin" />
                            </div>
                          )}
                        </div>
                      )}
                      <div className="min-w-0 flex-grow">
                        <h3 className="font-medium text-xs sm:text-sm mb-0.5 sm:mb-1 line-clamp-1">
                          {listing.title}
                        </h3>
                        <p className="text-xs sm:text-sm font-bold text-primary mb-0.5 sm:mb-1">
                          KES {parseFloat(listing.price).toLocaleString()}
                        </p>
                        <p className="text-[10px] sm:text-xs text-muted-foreground line-clamp-1">
                          {listing.subCounty}, {listing.county}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <Button
              onClick={handleGenerate}
              disabled={selectedIds.length === 0 || generating}
              className="w-full h-11 sm:h-12 text-sm sm:text-base font-medium"
            >
              {generating ? (
                <>
                  <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Eye className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                  Generate Facebook Collage
                </>
              )}
            </Button>
          </CardContent>
        )}
      </Card>

      {showPreview && generatedData && (
        <>
          <Card className="shadow-sm">
            <CardHeader className="p-3 sm:p-4 md:p-6">
              <CardTitle className="text-sm sm:text-base md:text-lg">Preview</CardTitle>
            </CardHeader>
            <CardContent className="p-3 sm:p-4 md:p-6 pt-0">
              <div className="border-2 rounded-lg overflow-hidden bg-white shadow-lg">
                <canvas 
                  ref={canvasRef}
                  className="w-full h-auto"
                />
              </div>
            </CardContent>
          </Card>

          <Card className="border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50 shadow-sm">
            <CardHeader className="p-3 sm:p-4 md:p-6">
              <CardTitle className="text-sm sm:text-base md:text-lg text-blue-900">
                Share on Facebook
              </CardTitle>
            </CardHeader>
            <CardContent className="p-3 sm:p-4 md:p-6 pt-0 space-y-3 sm:space-y-4">
              
              <div className="space-y-2 sm:space-y-3">
                <Button
                  onClick={handleDownloadCollage}
                  variant="outline"
                  className="w-full h-11 sm:h-12 border-blue-300 hover:bg-blue-100 text-xs sm:text-sm font-medium"
                >
                  {imageDownloaded ? (
                    <>
                      <Check className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-green-600" />
                      <span className="text-green-600">Image Downloaded ✓</span>
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                      Step 1: Download Image
                    </>
                  )}
                </Button>

                <Button
                  onClick={handleCopyMessage}
                  variant="outline"
                  className="w-full h-11 sm:h-12 border-blue-300 hover:bg-blue-100 text-xs sm:text-sm font-medium"
                >
                  {messageCopied ? (
                    <>
                      <Check className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-green-600" />
                      <span className="text-green-600">Message Copied ✓</span>
                    </>
                  ) : (
                    <>
                      <Share2 className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                      Step 2: Copy Message
                    </>
                  )}
                </Button>

                <Button
                  onClick={() => window.open('https://www.facebook.com', '_blank')}
                  variant="outline"
                  className="w-full h-11 sm:h-12 border-blue-300 hover:bg-blue-100 text-xs sm:text-sm font-medium"
                >
                  <Share2 className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                  Step 3: Open Facebook
                </Button>
              </div>

              <Alert className="border-blue-300 bg-blue-50">
                <AlertCircle className="h-4 w-4 text-blue-700" />
                <AlertDescription className="text-[10px] sm:text-xs text-blue-900">
                  <strong className="font-bold">Simple steps:</strong>
                  <ol className="mt-2 space-y-1 list-decimal list-inside">
                    <li>Click download to save image</li>
                    <li>Click copy to copy message with hashtags</li>
                    <li>Open Facebook (Timeline/Groups/Marketplace)</li>
                    <li>Create post, attach image, paste message, and publish</li>
                  </ol>
                </AlertDescription>
              </Alert>

              <p className="text-[10px] sm:text-xs text-center text-muted-foreground">
                Message includes tracking links back to motohomefinder.com
              </p>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}